import { Dropdown, type MenuProps } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import contactIcon from '@/assets/imgs/header/contact.svg';
import dropdownArrow from '@/assets/imgs/header/dropdown-arrow.svg';
import languageGlobe from '@/assets/imgs/header/language-globe.svg';
import languageSelected from '@/assets/imgs/header/language-selected.svg';
import logo from '@/assets/imgs/header/logo.png';
import { supportedLanguages, type SupportedLanguage } from '@/locales';

const arrow = (
  <span className="flex size-[16px] shrink-0 items-center justify-center" aria-hidden="true">
    <img src={dropdownArrow} alt="" className="h-[6px] w-[11px]" />
  </span>
);

const navigationMenuStyles: NonNullable<MenuProps['styles']> = {
  root: {
    minWidth: '7.5rem',
    padding: 0,
    overflow: 'hidden',
    borderRadius: '0.75rem',
    boxShadow:
      '0 0.352625rem 0.8815rem rgba(0, 0, 0, 0.05), 0 0.147375rem 0.3684375rem rgba(0, 0, 0, 0.13)',
  },
  item: {
    minHeight: '2.5rem',
    padding: '0.5rem 1rem',
    borderRadius: '2.5rem',
  },
  itemTitle: {
    color: 'rgba(51, 51, 51, 0.8)',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: '1.5rem',
  },
};

type HeaderProps = {
  transparentAtTop?: boolean;
};

const languageOptions: Array<{
  value: SupportedLanguage;
  flag: string;
  label: string;
}> = [
  { value: 'zh-CN', flag: '🇨🇳', label: '中文' },
  { value: 'ja-JP', flag: '🇯🇵', label: '日本語' },
  { value: 'en-US', flag: '🇺🇸', label: 'English' },
];

export function Header({ transparentAtTop = false }: HeaderProps) {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(() => window.scrollY > 0);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const showTransparentBackground = transparentAtTop && !isScrolled;
  const resolvedLanguage = supportedLanguages.includes(
    i18n.resolvedLanguage as SupportedLanguage,
  )
    ? (i18n.resolvedLanguage as SupportedLanguage)
    : 'zh-CN';
  const currentLanguage =
    languageOptions.find((language) => language.value === resolvedLanguage) ??
    languageOptions[0];

  useEffect(() => {
    const updateHeaderBackground = () => setIsScrolled(window.scrollY > 0);

    updateHeaderBackground();
    window.addEventListener('scroll', updateHeaderBackground, { passive: true });

    return () => window.removeEventListener('scroll', updateHeaderBackground);
  }, []);

  const aboutMenu: MenuProps['items'] = [
    {
      key: '/about#ceo-message',
      label: <Link className="block text-[16px] leading-[24px]" to="/about#ceo-message">{t('common.footer.ceoMessage')}</Link>,
    },
    {
      key: '/about#vision',
      label: <Link className="block text-[16px] leading-[24px]" to="/about#vision">{t('common.footer.companyVision')}</Link>,
    },
    {
      key: '/about#profile',
      label: <Link className="block text-[16px] leading-[24px]" to="/about#profile">{t('common.footer.companyProfile')}</Link>,
    },
    {
      key: '/about#members',
      label: <Link className="block text-[16px] leading-[24px]" to="/about#members">{t('common.footer.members')}</Link>,
    },
  ];

  const serviceMenu: MenuProps['items'] = [
    { key: '/travel', label: <Link className="block text-[16px] leading-[24px]" to="/travel">Bilionpass Travels</Link> },
    { key: '/shop', label: <Link className="block text-[16px] leading-[24px]" to="/shop">Bilionpass Shops</Link> },
    { key: 'vistapath-marketing', disabled: true, label: <span className="block cursor-default text-[16px] leading-[24px] text-[#a3a3a3]">{t('common.footer.marketing')}</span> },
    {
      key: 'vistapath-hr',
      label: (
        <a
          href="https://www.vistapath.co.jp"
          target="_blank"
          rel="noreferrer"
          className="block text-[16px] leading-[24px]"
        >
          {t('common.footer.hr')}
        </a>
      ),
    },
  ];

  const linkClassName = (active: boolean) =>
    `whitespace-nowrap text-[20px] font-normal leading-[30px] transition-colors hover:text-[#00A0E9] ${
      active ? 'text-[#00A0E9]' : 'text-[#000]'
    }`;

  const languageMenu = (
    <div
      className="h-[120px] w-[150px] overflow-hidden rounded-[12px] bg-white shadow-[0_5.642px_14.104px_rgba(0,0,0,0.05),0_2.358px_5.895px_rgba(0,0,0,0.13)]"
      role="menu"
      aria-label="Language"
    >
      <div className="flex flex-col items-start size-full">
        {languageOptions.map((language) => {
          const selected = language.value === resolvedLanguage;

          return (
            <button
              key={language.value}
              type="button"
              role="menuitemradio"
              aria-checked={selected}
              onClick={() => {
                setIsLanguageMenuOpen(false);
                if (!selected) void i18n.changeLanguage(language.value);
              }}
              className={`flex h-[40px] w-full shrink-0 cursor-pointer items-center gap-[8px] border-0 px-[16px] py-[8px] text-left text-[rgba(51,51,51,0.8)] outline-none transition-colors hover:bg-[rgba(0,160,233,0.06)] ${
                selected ? 'rounded-none' : 'rounded-[40px]'
              }`}
              style={{ backgroundColor: selected ? 'rgba(0, 160, 233, 0.1)' : '#fff' }}
            >
              <span className="w-[20px] shrink-0 text-[20px] leading-[24px] mr-[8px]" aria-hidden="true">
                {language.flag}
              </span>
              <span className="min-w-0 flex-1 text-[16px] leading-[24px]">
                {language.label}
              </span>
              {selected && (
                <img
                  src={languageSelected}
                  alt=""
                  className="size-[16px] shrink-0"
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 flex h-[80px] min-w-[1480px] w-full items-center px-0 py-[20px] transition-[background-color,box-shadow] duration-300 ${
        showTransparentBackground
          ? 'bg-transparent shadow-none'
          : 'bg-white'
      }`}
    >
      <div className="mx-auto flex w-full max-w-[1480px] items-center justify-between">
        <div className="flex h-[80px] shrink-0 items-center">
          <Link
            to="/"
            className="flex h-[44px] w-[172px] shrink-0 items-center"
            aria-label={t('common.accessibility.home')}
          >
            <img src={logo} alt="BilionPass" className="object-contain w-full h-auto" />
          </Link>
        </div>

        <nav className="flex items-center gap-[70px]" aria-label={t('common.accessibility.mainNavigation')}>
          <Link to="/" className={linkClassName(pathname === '/')}>
            {t('common.navigation.home')}
          </Link>

          <Dropdown
            menu={{ items: aboutMenu, styles: navigationMenuStyles }}
            rootClassName="header-navigation-dropdown"
            placement="bottom"
            trigger={['hover']}
            align={{ offset: [0, 8] }}
          >
            <button
              type="button"
              className={`${linkClassName(pathname === '/about')} flex items-center gap-[4px] border-0 bg-transparent p-0`}
            >
              {t('common.navigation.aboutUs')}
              {arrow}
            </button>
          </Dropdown>

          <Dropdown
            menu={{ items: serviceMenu, styles: navigationMenuStyles }}
            rootClassName="header-navigation-dropdown"
            placement="bottom"
            trigger={['hover']}
            align={{ offset: [0, 8] }}
          >
            <button
              type="button"
              className={`${linkClassName(pathname === '/travel' || pathname === '/shop')} flex items-center gap-[4px] border-0 bg-transparent p-0`}
            >
              {t('common.navigation.business')}
              {arrow}
            </button>
          </Dropdown>

          <Link to="/news" className={linkClassName(pathname.startsWith('/news'))}>
            {t('common.navigation.news')}
          </Link>

          <Link to="/recruit" className={linkClassName(pathname.startsWith('/recruit'))}>
            {t('common.navigation.careers')}
          </Link>

          <div className="flex shrink-0 items-center gap-[16px]">
            <Link
              to="/contact"
              className="group flex h-[46px] shrink-0 items-center justify-center overflow-hidden rounded-[94.565px] border border-solid border-[#ff5d21] bg-[#f9780b] px-[16px] py-[8px] text-[20px] font-normal whitespace-nowrap text-white transition-[background-color,color] duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-white hover:text-[#f9780b] motion-reduce:duration-0"
            >
              <span className="flex items-center gap-[8px] whitespace-nowrap transition-transform duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-x-[14px] motion-reduce:duration-0">
                <img
                  src={contactIcon}
                  alt=""
                  className="size-[20px] shrink-0 opacity-100 transition-opacity duration-[120ms] group-hover:opacity-0 motion-reduce:duration-0"
                />
                {t('common.navigation.contactUs')}
              </span>
            </Link>

            <span className="inline-block h-[20px] w-px shrink-0 bg-[#aeb7c0]" aria-hidden="true" />

            <Dropdown
              open={isLanguageMenuOpen}
              onOpenChange={setIsLanguageMenuOpen}
              popupRender={() => languageMenu}
              placement="bottomRight"
              align={{ offset: [0, 8] }}
              trigger={['hover']}
            >
              <button
                type="button"
                className="flex h-[40px] min-w-[120px] shrink-0 cursor-pointer items-center gap-[8px] rounded-[40px] border-0 bg-white px-[16px] py-[8px] text-[rgba(51,51,51,0.8)] outline-none"
                aria-haspopup="menu"
                aria-expanded={isLanguageMenuOpen}
              >
                <img src={languageGlobe} alt="" className="size-[24px] shrink-0" />
                <span className="text-[16px] leading-[24px]">{currentLanguage.label}</span>
                <span
                  className={`flex size-[16px] shrink-0 items-center justify-center transition-transform duration-200 ${
                    isLanguageMenuOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                >
                  <img src={dropdownArrow} alt="" className="h-[6px] w-[11px]" />
                </span>
              </button>
            </Dropdown>
          </div>
        </nav>
      </div>
    </header>
  );
}
